import React, { useCallback, useState } from "react";
import { Alert, Button, Card, Checkbox, Divider, Row, Typography } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { CompanyContact } from "@bindeleddet/core/types";
import { isCompanyUser, useLoginContext } from "@bindeleddet/core/contexts/login";
import ContactSection from "../ContactSection/ContactSection";
import { PriceTier } from "../interface";
import PriceReceipt from "../PriceReceipt/PriceReceipt";
import Terms from "./Terms";

const { Title, Text } = Typography;

interface Props {
  priceTiers: PriceTier[];
  selectedDate: Date | undefined;
  loading: boolean;
  placeBooking: () => void;
  goToPreviousStep: () => void;
}

const TermsAndContact: React.FC<Props> = ({ priceTiers, selectedDate, loading, placeBooking, goToPreviousStep }) => {
  const [accepted, setAccepted] = useState<boolean>(false);
  // undefined until the contact persons are loaded; then the chosen contact person, or null if none is chosen
  const [currentContact, setCurrentContact] = useState<CompanyContact | null>();
  const handleContactChange = useCallback(
    (contact: CompanyContact | undefined) => setCurrentContact(contact ?? null),
    []
  );
  const contactProblem =
    currentContact === null
      ? "Velg en kontaktperson før dere kan booke. Dere kan velge en eksisterende eller opprette en ny."
      : currentContact && !currentContact.email
      ? "Kontaktpersonen mangler e-postadresse. Legg den inn ved å trykke «Endre» og redigere kontaktpersonen, eller velg en annen."
      : undefined;
  const canBook = accepted && currentContact !== undefined && contactProblem === undefined;
  const { currentUser } = useLoginContext();

  const handleTerms = (e: CheckboxChangeEvent) => {
    setAccepted(e.target.checked);
  };

  if (!isCompanyUser(currentUser)) return null;
  return (
    <>
      <Card style={{ width: "100%" }}>
        <Title level={5}>Valgt dato og pris</Title>
        <PriceReceipt tiers={priceTiers} selectedDate={selectedDate} style={{ maxWidth: "520px" }} />
        <Divider />
        <Title level={5}>Kontaktperson for arrangementet</Title>
        <Text type="secondary" style={{ display: "block", marginBottom: "12px" }}>
          Bindeleddet tar kontakt med denne personen i forkant av arrangementet, og bekreftelsen på bookingen sendes til
          e-postadressen.
        </Text>
        {contactProblem && (
          <Alert type="warning" showIcon message={contactProblem} style={{ marginBottom: "12px", maxWidth: "520px" }} />
        )}
        <ContactSection companyId={currentUser.id} companyName={currentUser.name} onChange={handleContactChange} />
        <Divider />
        <Title level={5}>
          Vilkår{" "}
          <Text type="secondary" style={{ fontSize: "14px", fontWeight: "normal" }}>
            (må godtas for å booke)
          </Text>
        </Title>
        <Checkbox checked={accepted} onChange={handleTerms}>
          Jeg godtar <Terms />.
        </Checkbox>
        {!accepted && (
          // Shown as text, since the hover title of a disabled button isn't shown in most browsers
          <Text type="secondary" style={{ display: "block", fontSize: "12px", marginTop: "4px" }}>
            Dere må godta vilkårene før dere kan booke.
          </Text>
        )}
        {accepted && contactProblem && (
          <Text type="secondary" style={{ display: "block", fontSize: "12px", marginTop: "4px" }}>
            Velg en kontaktperson med e-postadresse over før dere kan booke.
          </Text>
        )}
      </Card>
      <Row style={{ justifyContent: "space-between", width: "100%", padding: "10px 0" }}>
        <Button type="default" size="large" onClick={goToPreviousStep}>
          Forrige
        </Button>
        <Button
          type="primary"
          size="large"
          loading={loading}
          disabled={!canBook}
          // Antd's disabled styling must win, so the orange is only applied when the button is enabled
          style={
            canBook ? { backgroundColor: "var(--secondary-color)", borderColor: "var(--secondary-color)" } : undefined
          }
          onClick={placeBooking}
        >
          Book dato
        </Button>
      </Row>
    </>
  );
};

export default TermsAndContact;
